import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Key, LockKey, LockKeyOpen, ShieldCheck, CheckCircle, CircleNotch, XCircle,
  Prohibit, Play, Lightning, ArrowRight, BookOpen,
} from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

function StepBadge({ n, done, active }) {
  return (
    <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border
      ${done ? "bg-emerald-600 border-emerald-600 text-white"
             : active ? "bg-[#071B3E] border-[#071B3E] text-white" : "bg-muted border-border text-muted-foreground"}`}>
      {done ? <CheckCircle size={16} weight="fill" /> : n}
    </div>
  );
}

export default function KeySecurityOnboarding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bcsi/my/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load key status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      await api.post("/bcsi/my/connect-key", {});
      toast.success("Your customer-managed key is connected — you're now at L2.");
      setTestResult(null);
      await load();
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      toast.error(formatApiError(e.response?.data?.detail) || "Connect failed");
    } finally { setBusy(""); }
  };

  const runTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/bcsi/my/test");
      setTestResult(data);
      if (data.ok) toast.success("Self-test passed — encryption round-trip verified.");
      else toast.error("Self-test failed — see the step details below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
    } finally { setBusy(""); }
  };

  const toggleKey = async (enable) => {
    setBusy(enable ? "enable" : "revoke");
    try {
      await api.post(`/bcsi/my/${enable ? "enable" : "revoke"}`);
      toast.success(enable ? "Key re-enabled — your data is readable again." : "Key revoked — your data is now dark, even to us.");
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(""); }
  };

  const connected = !!status?.key_connected;
  const enabled = status?.key_enabled;

  if (forbidden) {
    return (
      <div className="border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="keyonboard-forbidden">
        Encryption keys can only be managed by a workspace administrator.
      </div>
    );
  }

  return (
    <div className="border border-border bg-card" data-testid="key-security-onboarding">
      <div className="px-5 py-4 border-b border-border bg-[#071B3E] text-white flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-lg bg-white/10 flex items-center justify-center">
          <Key size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold">Your Encryption Key (BCSI Data Protection)</h3>
          <p className="text-[13px] text-white/70 mt-0.5 leading-snug max-w-2xl">
            Connect a customer-managed key so your sensitive BCSI data is sealed with a key you control.
            Revoke it at any time and your data goes dark — even to us. Set it up and test it yourself in two steps.
          </p>
          <button onClick={() => navigate("/guide/bcsi-key-setup")} data-testid="keyonboard-guide-link"
            className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-white/90 underline underline-offset-2 hover:text-white">
            <BookOpen size={15} weight="bold" /> Read the full setup guide
          </button>
        </div>
        {connected && (
          <span className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold border rounded-sm
            ${enabled ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" : "bg-red-500/20 text-red-200 border-red-400/40"}`}
            data-testid="keyonboard-level-chip">
            {enabled ? <LockKey size={13} weight="fill" /> : <Prohibit size={13} weight="fill" />}
            {enabled ? "L2 · Key active" : "L2 · Key revoked (dark)"}
          </span>
        )}
        {!connected && !loading && (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold border rounded-sm bg-white/10 text-white/80 border-white/20" data-testid="keyonboard-level-chip">
            <LockKeyOpen size={13} weight="bold" /> L0 · Platform-managed
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-5 text-sm text-muted-foreground" data-testid="keyonboard-loading">Loading key status…</div>
      ) : (
        <div className="p-5 space-y-5">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5">
            Demo environment: your key is held in a local simulated key service. In production this becomes your
            own cloud KMS key (AWS KMS / BYOK) with the same two-step setup.
          </p>

          {/* Step 1 — Connect */}
          <div className="flex gap-3" data-testid="keyonboard-step-1">
            <StepBadge n={1} done={connected} active={!connected} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-foreground">Connect your key</div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                Provision a customer-managed key (CMEK/BYOK) for your workspace. This wraps a per-tenant data key
                that encrypts your BCSI fields. Moves you from L0 (platform-managed) to L2 (customer-managed).
              </p>
              <div className="mt-2">
                {!connected ? (
                  <Button className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]" onClick={connect} disabled={busy === "connect"} data-testid="keyonboard-connect-btn">
                    <LockKey size={15} className="mr-1.5" /> {busy === "connect" ? "Connecting…" : "Connect my key"}
                  </Button>
                ) : (
                  <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium" data-testid="keyonboard-connected-note">
                    <CheckCircle size={15} weight="fill" /> Connected · key reference <span className="font-mono">{status.cmk_ref}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Step 2 — Test */}
          <div className={`flex gap-3 ${!connected ? "opacity-50" : ""}`} data-testid="keyonboard-step-2">
            <StepBadge n={2} done={!!testResult?.ok} active={connected && !testResult?.ok} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-foreground">Test your key</div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                Run a live encrypt-then-decrypt round-trip to confirm your key can seal and unseal data correctly.
              </p>
              <div className="mt-2">
                <Button variant="outline" className="rounded-none" onClick={runTest} disabled={!connected || busy === "test"} data-testid="keyonboard-test-btn">
                  <Lightning size={15} className="mr-1.5" /> {busy === "test" ? "Testing…" : "Run self-test"}
                </Button>
              </div>

              {testResult && (
                <div className="mt-3 border border-border" data-testid="keyonboard-test-result">
                  <div className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
                    {testResult.ok ? <CheckCircle size={15} weight="fill" /> : <XCircle size={15} weight="fill" />}
                    {testResult.ok ? "Round-trip verified — your key works." : "Round-trip failed — your key is not usable right now."}
                  </div>
                  <ul className="divide-y divide-border">
                    {(testResult.steps || []).map((s) => (
                      <li key={s.step} className="px-3 py-1.5 text-xs flex items-center gap-2" data-testid={`keyonboard-test-step-${s.step}`}>
                        {s.ok ? <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                              : <XCircle size={14} weight="fill" className="text-red-600 shrink-0" />}
                        <span className="text-foreground">{s.label}</span>
                        {s.detail && <span className="text-red-600">— {s.detail}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Sovereignty control */}
          {connected && (
            <div className="border-t border-border pt-4" data-testid="keyonboard-sovereignty">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck size={18} weight="duotone" className="text-[#071B3E]" />
                  <span className="text-foreground font-medium">You control access to your data.</span>
                  <span className="text-muted-foreground text-xs">Revoking your key instantly makes your BCSI data unreadable — to anyone, including us.</span>
                </div>
                {enabled ? (
                  <Button variant="outline" className="rounded-none text-red-700 border-red-300 hover:bg-red-50" onClick={() => toggleKey(false)} disabled={busy === "revoke"} data-testid="keyonboard-revoke-btn">
                    <Prohibit size={15} className="mr-1.5" /> {busy === "revoke" ? "Revoking…" : "Revoke my key (go dark)"}
                  </Button>
                ) : (
                  <Button variant="outline" className="rounded-none text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => toggleKey(true)} disabled={busy === "enable"} data-testid="keyonboard-enable-btn">
                    <Play size={15} className="mr-1.5" /> {busy === "enable" ? "Re-enabling…" : "Re-enable my key"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
